import type { UIEvent } from 'react';
import {
  Alert,
  Descriptions,
  Drawer,
  Empty,
  Flex,
  List,
  Skeleton,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { deriveSettingTranslationKey } from '../../utils';
import { useSettingHistory } from './hooks/useSettingHistory';

export interface SettingHistoryDrawerProps {
  settingKey: string | null;
  onClose: () => void;
}

export function SettingHistoryDrawer({
  settingKey,
  onClose,
}: SettingHistoryDrawerProps) {
  const { t, i18n } = useTranslation('settings');
  const { items, total, isLoading, isFetchingNextPage, hasError, onScroll } =
    useSettingHistory(settingKey);
  const title = settingKey
    ? t(deriveSettingTranslationKey(settingKey, 'label'), {
        defaultValue: settingKey,
      })
    : '';
  const showStatusFooter = !isLoading && !hasError;

  function handleScroll(event: UIEvent<HTMLDivElement>): void {
    onScroll(event);
  }

  return (
    <Drawer
      open={Boolean(settingKey)}
      width={560}
      title={t('history.title', { setting: title })}
      onClose={onClose}
      styles={{
        content: {
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
        body: {
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          paddingTop: 12,
          paddingBottom: 12,
        },
      }}
      footer={
        showStatusFooter ? (
          <Flex align="center" justify="space-between" gap={12}>
            <Typography.Text type="secondary">
              {t('history.showing', {
                count: items.length,
                total,
              })}
            </Typography.Text>
            {isFetchingNextPage ? <Spin size="small" /> : null}
          </Flex>
        ) : null
      }
    >
      <div className="platform-settings-history-scroll" onScroll={handleScroll}>
        {isLoading ? <Skeleton active /> : null}
        {hasError ? (
          <Alert type="error" showIcon message={t('errors.history')} />
        ) : null}
        {!isLoading && !hasError && items.length === 0 ? (
          <Empty description={t('history.empty')} />
        ) : null}
        {!isLoading && !hasError && items.length > 0 ? (
          <List
            dataSource={[...items]}
            renderItem={(item) => (
              <List.Item className="platform-settings-history-item">
                <Descriptions size="small" column={1}>
                  <Descriptions.Item label={t('history.changedAt')}>
                    {item.changedAt.toLocaleString(i18n.language)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('history.version')}>
                    <Tag>{`${item.fromVersion} → ${item.toVersion}`}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('history.change')}>
                    <span>
                      <code>
                        {item.previousValue === undefined
                          ? t('history.notAvailable')
                          : String(item.previousValue)}
                      </code>
                      {' → '}
                      <code>{String(item.newValue)}</code>
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('history.reason')}>
                    {item.reason || t('history.noReason')}
                  </Descriptions.Item>
                </Descriptions>
              </List.Item>
            )}
          />
        ) : null}
      </div>
    </Drawer>
  );
}
